import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Space, message, Popconfirm, Card, Upload, Tooltip, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import './index.less';

const { TextArea } = Input;

interface Character {
  id: number;
  code: string;
  hash: string;
  name: string;
  description: string;
  imageUrl: string;
  syntheticImageUrl: string;
  isSynthetic: boolean;
  syntheticReason: string;
  created: string;
}

const SyntheticAdmin: React.FC = () => {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null);
  const [form] = Form.useForm();
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);
  const [syntheticImageFileList, setSyntheticImageFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    loadCharacters();
  }, []);

  const loadCharacters = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/synthetic/admin/characters');
      const data = await response.json();
      if (data.success) {
        setCharacters(data.data);
      } else {
        message.error('加载失败');
      }
    } catch (error) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCharacter = () => {
    setEditingCharacter(null);
    form.resetFields();
    setImageFileList([]);
    setSyntheticImageFileList([]);
    setModalVisible(true);
  };

  const handleEditCharacter = (character: Character) => {
    setEditingCharacter(character);

    // 设置表单值
    form.setFieldsValue({
      code: character.code,
      name: character.name,
      description: character.description,
      isSynthetic: character.isSynthetic,
      syntheticReason: character.syntheticReason,
    });

    // 设置已有图片
    if (character.imageUrl) {
      setImageFileList([{
        uid: '-1',
        name: 'image.png',
        status: 'done',
        url: character.imageUrl,
      }]);
    } else {
      setImageFileList([]);
    }

    if (character.syntheticImageUrl) {
      setSyntheticImageFileList([{
        uid: '-2',
        name: 'synthetic-image.png',
        status: 'done',
        url: character.syntheticImageUrl,
      }]);
    } else {
      setSyntheticImageFileList([]);
    }

    setModalVisible(true);
  };

  const handleDeleteCharacter = async (id: number) => {
    try {
      const response = await fetch(`/api/v1/synthetic/admin/characters/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        message.success('删除成功');
        loadCharacters();
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 处理图片上传
  const handleImageUpload = async (file: File, type: string): Promise<string> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch('/api/v1/synthetic/admin/upload-image', {
      method: 'POST',
      body: formData
    });

    const data = await response.json();
    if (data.success) {
      return data.data.url;
    } else {
      throw new Error(data.message || '上传失败');
    }
  };

  const handleSubmitCharacter = async () => {
    try {
      const values = await form.validateFields();

      // 验证图片必须上传
      if (imageFileList.length === 0) {
        message.error('请上传图片');
        return;
      }

      // 处理图片上传
      if (imageFileList.length > 0 && imageFileList[0].originFileObj) {
        message.loading('正在上传图片...', 0);
        try {
          const imageUrl = await handleImageUpload(imageFileList[0].originFileObj as File, 'image');
          values.imageUrl = imageUrl;
        } catch (error) {
          message.destroy();
          message.error('图片上传失败');
          return;
        }
        message.destroy();
      } else if (imageFileList.length > 0 && imageFileList[0].url) {
        values.imageUrl = imageFileList[0].url;
      }

      // 处理伪人形态图片上传（可选）
      if (syntheticImageFileList.length > 0 && syntheticImageFileList[0].originFileObj) {
        message.loading('正在上传伪人形态图片...', 0);
        try {
          const imageUrl = await handleImageUpload(syntheticImageFileList[0].originFileObj as File, 'synthetic');
          values.syntheticImageUrl = imageUrl;
        } catch (error) {
          message.destroy();
          message.error('伪人形态图片上传失败');
          return;
        }
        message.destroy();
      } else if (syntheticImageFileList.length > 0 && syntheticImageFileList[0].url) {
        values.syntheticImageUrl = syntheticImageFileList[0].url;
      } else {
        // 如果没有图片，设置为空字符串
        values.syntheticImageUrl = '';
      }

      const url = editingCharacter
        ? `/api/v1/synthetic/admin/characters/${editingCharacter.id}`
        : '/api/v1/synthetic/admin/characters';
      const method = editingCharacter ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values)
      });

      const data = await response.json();
      if (data.success) {
        message.success(editingCharacter ? '更新成功' : '创建成功');
        setModalVisible(false);
        loadCharacters();
      } else {
        message.error(data.message || '操作失败');
      }
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const characterColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      sorter: (a: Character, b: Character) => a.id - b.id,
      defaultSortOrder: 'ascend' as const
    },
    { title: '编码', dataIndex: 'code', key: 'code', width: 120 },
    {
      title: 'Hash',
      dataIndex: 'hash',
      key: 'hash',
      width: 80,
      render: (hash: string) => hash || '-'
    },
    {
      title: '扫码链接',
      key: 'scanUrl',
      width: 150,
      render: (record: Character) => (
        record.hash ? (
          <a href={`https://ds2.cn/st/${record.hash}`} target="_blank" rel="noopener noreferrer">
            ds2.cn/st/{record.hash}
          </a>
        ) : '-'
      )
    },
    { title: '名称', dataIndex: 'name', key: 'name', width: 150 },
    {
      title: '图片',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 100,
      render: (url: string) => (
        url ? (
          <img src={url} alt="" style={{ width: 50, height: 67, objectFit: 'cover', borderRadius: 4 }} />
        ) : <span style={{ color: '#999' }}>-</span>
      )
    },
    {
      title: '伪人形态',
      dataIndex: 'syntheticImageUrl',
      key: 'syntheticImageUrl',
      width: 100,
      render: (url: string) => (
        url ? (
          <img src={url} alt="" style={{ width: 50, height: 67, objectFit: 'cover', borderRadius: 4 }} />
        ) : <span style={{ color: '#999' }}>-</span>
      )
    },
    {
      title: '是否伪人',
      dataIndex: 'isSynthetic',
      key: 'isSynthetic',
      width: 80,
      render: (isSynthetic: boolean) => (
        <span style={{ color: isSynthetic ? '#ff4d4f' : '#52c41a' }}>
          {isSynthetic ? '是' : '否'}
        </span>
      )
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text || '-'}</div>
      )
    },
    {
      title: '伪人理由',
      dataIndex: 'syntheticReason',
      key: 'syntheticReason',
      render: (text: string) => (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text || '-'}</div>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right' as const,
      render: (record: Character) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Tooltip title="编辑">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditCharacter(record)}
              style={{ width: '100%' }}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定要删除吗？"
              onConfirm={() => handleDeleteCharacter(record.id)}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ width: '100%' }}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div className="synthetic-admin">
      <Card
        title="伪人游戏管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateCharacter}>
            创建新人物
          </Button>
        }
        headStyle={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#fff' }}
      >
        <Table
          columns={characterColumns}
          dataSource={characters}
          loading={loading}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* 人物编辑 Modal */}
      <Modal
        title={editingCharacter ? '编辑人物' : '创建人物'}
        open={modalVisible}
        onOk={handleSubmitCharacter}
        onCancel={() => setModalVisible(false)}
        width={800}
        style={{ top: 20 }}
      >
        <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
          <style>{`
            .ant-form-item {
              margin-bottom: 15px;
            }
          `}</style>
          <Form.Item
            name="code"
            label="编码"
            rules={[{ required: true, message: '请输入编码' }]}
          >
            <Input placeholder="唯一编码，用于扫码" />
          </Form.Item>

          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder="人物名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="人物描述（可选）" />
          </Form.Item>

          <Form.Item
            name="isSynthetic"
            label="是否伪人"
            valuePropName="checked"
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>

          <Form.Item
            name="syntheticReason"
            label="伪人的理由"
          >
            <TextArea rows={2} placeholder="如果是伪人，填写理由" />
          </Form.Item>

          <Form.Item
            label="图片"
            required
            extra="人物图片"
          >
            <Upload
              listType="picture-card"
              fileList={imageFileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setImageFileList(fileList.slice(-1))}
              maxCount={1}
              accept="image/*"
            >
              {imageFileList.length === 0 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传图片</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item
            label="伪人形态图片"
            extra="伪人被识破后变异的样子（非伪人可不填）"
          >
            <Upload
              listType="picture-card"
              fileList={syntheticImageFileList}
              beforeUpload={() => false}
              onChange={({ fileList }) => setSyntheticImageFileList(fileList.slice(-1))}
              onRemove={() => {
                setSyntheticImageFileList([]);
                return true;
              }}
              maxCount={1}
              accept="image/*"
            >
              {syntheticImageFileList.length === 0 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传图片</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SyntheticAdmin;
